<script lang="ts">
  import { getContext, onMount } from "svelte";
  import type {
    Stripe,
    Appearance,
    StripeElementStyle,
    StripeCardNumberElement,
    StripeCardCvcElement,
    StripeCardExpiryElement,
    StripeElementChangeEvent,
    StripeCardNumberElementChangeEvent,
  } from "@stripe/stripe-js";
  import {
    stripeStore,
    createSubscription,
    SubscriptionItem,
    updateUserInfo,
    handleError,
  } from "../../../../../app/api";
  import {
    CREATE_NEW_ACCOUNT_CONTEXT_NAME,
    PLAN_LOCAL_STORAGE_KEY,
  } from "../../utils";
  import type { CreateNewAccountContext } from "../../type";
  import {
    plansDataStore,
    getPlanBasePrice,
    getPlanPerSeatPrice,
  } from "../../../../../app/store/store-plan";
  import Icon from "../../../../common/icons/Icon.svelte";

  const context: CreateNewAccountContext = getContext(
    CREATE_NEW_ACCOUNT_CONTEXT_NAME
  );
  let isMounted: boolean = false;
  let stripe: Stripe;
  let cardNumberElement: StripeCardNumberElement;
  let cardNumberError: string = "";
  let cardCvcElement: StripeCardCvcElement;
  let cardCvcError: string = "";
  let cardExpiryElement: StripeCardExpiryElement;
  let cardExpiryError: string = "";
  let cardElementError: string = "";
  let cardBrandIcon: string = "paymentcard-unknown";
  let currentPlanData: string = "";
  let subscriptionData: any;

  const appearance: Appearance = {
    theme: "none",
    variables: {},
  };

  const elementStyle: StripeElementStyle = {
    base: {
      fontFamily: "Poppins",
      fontSize: "13px",
      color: "#0E0121",
      fontWeight: "500",
      "::placeholder": {
        color: "#A7B0B5",
        fontWeight: "300",
      },
    },
    invalid: {
      fontFamily: "Poppins",
      fontSize: "13px",
      color: "#0E0121",
      fontWeight: "500",
    },
  };

  function onCardNumberElementChange(
    event: StripeCardNumberElementChangeEvent
  ) {
    // console.log("card number change ====== ", event);
    cardNumberError = event.error ? "Invalid card number" : "";
    // 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'unionpay' | 'unknown'
    cardBrandIcon = event.brand
      ? `paymentcard-${event.brand}`
      : "paymentcard-unknown";
  }

  function onElementChange(event: StripeElementChangeEvent) {
    // console.log("Element ==== ", event.elementType, event);
    const elementType = event.elementType;
    if (elementType === "cardCvc") {
      cardCvcError = event.error ? "Invalid CVC" : "";
    } else if (elementType === "cardExpiry") {
      cardExpiryError = event.error ? "Invalid expiry date" : "";
    }
  }

  async function initCardPaymentForm() {
    const elements = stripe.elements({ appearance });
    cardNumberElement = elements.create("cardNumber", {
      placeholder: "Enter Your Card Number",
      style: elementStyle,
    });
    cardExpiryElement = elements.create("cardExpiry", {
      style: elementStyle,
    });
    cardCvcElement = elements.create("cardCvc", {
      style: elementStyle,
    });

    cardNumberElement.mount("#card-number-element");
    cardExpiryElement.mount("#card-expiry-element");
    cardCvcElement.mount("#card-cvc-element");

    cardNumberElement.on("change", onCardNumberElementChange);
    cardExpiryElement.on("change", onElementChange);
    cardCvcElement.on("change", onElementChange);
  }

  async function handleSubscribe() {
    const newPlanData = localStorage.getItem(PLAN_LOCAL_STORAGE_KEY);
    // if new plan data equal current plan data, no need to create new subscription
    if (subscriptionData && newPlanData === currentPlanData) {
      // console.log("Don't Create new subscription ============ ");
      return subscriptionData;
    }
    // console.log("Create new subscription ============ ");
    const accountType = context.getAccountType();
    const duration = context.getPlanDuration();
    const planTier = context.getPlanTier();
    const basePrice = getPlanBasePrice(accountType, planTier, duration);
    const items: SubscriptionItem[] = [];
    items.push({ price: basePrice.id, quantity: 1 });
    if (accountType === "organization") {
      const numberOfLicense = context.getNumberOfLicenses();
      const perSeatPrice = getPlanPerSeatPrice(planTier, duration);
      items.push({ price: perSeatPrice.id, quantity: numberOfLicense });
    }
    let isError = false;
    subscriptionData = await createSubscription(items, {
      onError: async (error) => {
        handleError(error, {
          onTooManyRequest: () => {
            cardElementError = "Too many payment requests. Please try again in a few minutes.";
          },
        });
        isError = true;
      }
    });
    if (!subscriptionData) throw new Error("Cannot create subscription.");
    currentPlanData = newPlanData;
    return subscriptionData;
  }

  export async function handleStripePayment() {
    try {
      const subscriptionData = await handleSubscribe();
      // console.log(subscriptionData);
      const result = await stripe.confirmCardPayment(
        subscriptionData.clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
          },
        }
      );

      if (result.error && result.error.type !== "validation_error") {
        cardElementError = result.error.message;
      } else {
        cardElementError = "";
      }

      if (!result.error) {
        localStorage.removeItem(PLAN_LOCAL_STORAGE_KEY);
        await updateUserInfo();
      }
    } catch (error) {
      // console.log("payment error ========= ", error);
    }
  }

  onMount(() => {
    isMounted = true;
    return () => {
      cardNumberElement &&
        cardNumberElement.off("change", onCardNumberElementChange);
      cardExpiryElement && cardExpiryElement.off("change", onElementChange);
      cardCvcElement && cardCvcElement.off("change", onElementChange);
    };
  });

  $: stripe = $stripeStore;
  $: if (stripe && $plansDataStore && isMounted) {
    initCardPaymentForm();
  }
</script>

<div class="input-card gap-4">
  <div class="input-field card-number">
    <p class="required">Card Number</p>
    <div class="input-container" class:input-error={!!cardNumberError}>
      <div class="error-message-container">{cardNumberError}</div>
      <div id="card-number-element" class="stripe-input-element" />
      <div class="card-brand-icon">
        <Icon icon={cardBrandIcon} width="27px" height="18px" />
      </div>
      <div class="error-border" />
    </div>
  </div>

  <div class="additional-info">
    <div class="input-field expiry-date">
      <p class="required">Expiry Date</p>
      <div class="input-container" class:input-error={!!cardExpiryError}>
        <div class="error-message-container">{cardExpiryError}</div>
        <div id="card-expiry-element" class="stripe-input-element" />
        <div class="postfix-icon">
          <Icon icon="date-icon" size="20px" />
        </div>
        <div class="error-border" />
      </div>
    </div>

    <div class="input-field cvc">
      <p class="required">CVC code</p>
      <div class="input-container" class:input-error={!!cardCvcError}>
        <div class="error-message-container">{cardCvcError}</div>
        <div id="card-cvc-element" class="stripe-input-element" />
        <div class="error-border" />
      </div>
    </div>
  </div>
</div>

<div class="h-10 -mt-4">
  {#if cardElementError}
    <span class="text-tertiary-error"> {cardElementError} </span>
  {/if}
</div>

<style lang="postcss">
  .input-container {
    @apply relative mb-6;
  }

  .input-container .stripe-input-element {
    @apply relative h-12 border border-solid border-light-100 rounded text-dark-300 font-medium bg-white;
    padding: 16px 44px 16px 16px;
  }

  .input-container:not(.input-error)
    .stripe-input-element:global(.StripeElement--focus) {
    @apply border-primary-datadocs-blue bg-primary-datadocs-blue bg-opacity-5;
  }

  .input-container.input-error .stripe-input-element {
    @apply bg-white border-tertiary-error;
  }

  .input-container:not(.input-error) .error-message-container,
  .input-container:not(.input-error) .error-border {
    @apply hidden;
  }

  .error-message-container {
    @apply absolute bg-tertiary-error text-white rounded-bl rounded-br text-[11px] font-medium;
    padding: 8px 8px 4px;
    left: 0;
    right: 0;
    top: calc(100% - 6px);
  }

  .error-border {
    @apply absolute top-0 left-0 bottom-0 right-0 border-2 rounded border-tertiary-error pointer-events-none;
  }

  .card-brand-icon {
    @apply absolute top-0 bottom-0 right-0 w-11 pl-1;
    @apply flex flex-row items-center;
  }

  .input-card {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 2fr));
    gap: 8px;
  }

  .input-card .additional-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(134px, 1fr));
    gap: 8px;
  }

  @media (max-width: 500px) {
    .input-card,
    .input-card .additional-info {
      @apply block gap-0;
    }

    .input-card .additional-info > div {
      margin-top: 8px !important;
    }
  }

  .input-field {
    margin-top: 0px !important;
  }
</style>
